<template>
	<div class="merchant-map-container">
		<!-- 搜索栏 -->
		<div class="search-bar">
			<div class="search-bar-content">
				<h1 style="flex: 1">{{ $t('storeList.title2') }}</h1>
				<div style="display: flex; text-align: end">
					<div style="flex: 1"></div>
					<input v-model="searchQuery" type="text" placeholder="搜索商户名称..." class="search-input" @input="handleSearch" />
					<button @click="handleSearch" class="search-button">
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
							<path
								d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
						</svg>
					</button>
				</div>
			</div>

			<!-- 商户列表（搜索结果） - 移动到搜索栏内部 -->
			<div class="merchant-list" v-if="searchQuery && filteredMerchants.length > 0">
				<div v-for="(merchant, index) in filteredMerchants" :key="index" class="merchant-item" @click="centerMap(merchant)">
					<div class="merchant-icon" :style="{ backgroundColor: merchant.color || '#007AFF' }"></div>
					<div class="merchant-info">
						<div class="merchant-name">{{ merchant.name }}</div>
						<div class="merchant-address">{{ merchant.address }}</div>
					</div>
				</div>
			</div>
		</div>

		<!-- 地图容器 -->
		<div id="baidu-map-container" class="map-wrapper"></div>
	</div>
</template>

<script>
import { listenEvent, removeListener } from '../netTool/eventBus.js';

export default {
	name: 'MerchantMap',
	data() {
		return {
			merchants: [],
			searchQuery: '',
			map: null, // 百度地图实例
			markers: [], // 所有标记点
			mapReady: false, // 地图初始化状态
			pendingStoresUpdate: null, // 待处理的店铺更新数据
			centerMarker: null, // 中心点标记
			currentCenter: null // 当前中心点数据
		};
	},
	computed: {
		filteredMerchants() {
			if (!this.searchQuery) return this.merchants;
			const query = this.searchQuery.toLowerCase();
			return this.merchants.filter((merchant) => merchant.name.toLowerCase().includes(query) || merchant.address.toLowerCase().includes(query));
		}
	},
	mounted() {
		this.initBaiduMap();
		this.loadStores();
		listenEvent('stores-updated', this.handleStoresUpdate);
		listenEvent('stores-click', this.handleStoresClick);
	},
	beforeDestroy() {
		removeListener('stores-updated', this.handleStoresUpdate);
		removeListener('stores-click', this.handleStoresClick);
		if (this.map) {
			this.map.destroy();
		}
	},
	methods: {
		initBaiduMap() {
			if (window.BMap) {
				this.initMapInstance();
				return;
			}

			const script = document.createElement('script');
			script.src = `https://api.map.baidu.com/api?v=3.0&ak=0lexwooG5Wu0ACpUYPtTqyDotfLhP1kQ&callback=onBMapReady`;
			script.onerror = () => console.error('百度地图加载失败');
			document.head.appendChild(script);

			window.onBMapReady = () => {
				this.initMapInstance();
				this.processPendingUpdates(); // 处理等待中的更新
			};
		},
		// 初始化地图实例
		initMapInstance() {
			this.map = new BMap.Map('baidu-map-container');
			const defaultPoint = new BMap.Point(116.404, 39.915);
			this.map.centerAndZoom(defaultPoint, 15);
			this.map.enableScrollWheelZoom();
			this.map.enableDragging();

			this.map.addControl(new BMap.NavigationControl());
			this.map.addControl(new BMap.ScaleControl());

			this.mapReady = true;
		},

		// 处理店铺数据更新（关键修改）
		handleStoresUpdate(stores) {
			if (!this.mapReady) {
				this.pendingStoresUpdate = stores; // 暂存数据
				return;
			}

			this.processStores(stores);
		},
		processPendingUpdates() {
			if (this.pendingStoresUpdate) {
				this.processStores(this.pendingStoresUpdate);
				this.pendingStoresUpdate = null;
			}
		},
		// 统一处理店铺数据
		processStores(stores) {
			this.merchants = stores.map((store) => ({
				...store,
				color: this.getRandomColor(),
				point: new BMap.Point(parseFloat(store.longitude) || 0, parseFloat(store.latitude) || 0)
			}));

			this.updateMapMarkers();

			if (this.merchants.length > 0) {
				this.setCenterMarker(this.merchants[0]);
			}
		},

		loadStores() {
			const data = sessionStorage.getItem('brandstore');
			if (data) {
				try {
					const stores = JSON.parse(data);
					this.merchants = stores.map((store) => ({
						...store,
						color: this.getRandomColor(),

						point: new BMap.Point(store.longitude || 0, store.latitude || 0)
					}));

					this.updateMapMarkers();

					if (this.merchants.length > 0) {
						this.setCenterMarker(this.merchants[0]);
					}
				} catch (e) {
					this.merchants = [];
				}
			}
		},
		updateMapMarkers() {
			this.clearMarkers();

			this.merchants.forEach((merchant) => {
				if (merchant.point) {
					const marker = this.createMarker(merchant);
					this.markers.push(marker);
					this.map.addOverlay(marker);
				}
			});
		},

		createMarker(merchant) {
			const point = merchant.point;
			const marker = new BMap.Marker(point);

			const label = new BMap.Label(merchant.name, {
				offset: new BMap.Size(20, -10)
			});
			label.setStyle({
				color: '#333',
				fontSize: '12px',
				border: 'none',
				backgroundColor: 'transparent'
			});
			marker.setLabel(label);

			marker.addEventListener('click', () => {
				this.setCenterMarker(merchant);
			});

			return marker;
		},

		setCenterMarker(merchant) {
			if (!merchant || !merchant.point) return;

			if (this.centerMarker) {
				this.map.removeOverlay(this.centerMarker);
			}

			this.currentCenter = merchant;
			const point = merchant.point;

			this.centerMarker = new BMap.Marker(point, {
				icon: new BMap.Icon('https://api.map.baidu.com/images/markers.png', new BMap.Size(23, 25), {
					offset: new BMap.Size(10, 25),
					imageOffset: new BMap.Size(0, 0)
				})
			});

			this.map.addOverlay(this.centerMarker);
			this.map.panTo(point);

			const infoWindow = new BMap.InfoWindow(`
		        <div style="padding: 10px;">
		          <h4 style="margin: 0 0 5px 0;">${merchant.name}</h4>
		          <p style="margin: 5px 0;">地址: ${merchant.address}</p>
		          <p style="margin: 5px 0;">营业时间: ${merchant.businessHours}</p>
		          <p style="margin: 5px 0;">电话: ${merchant.phone}</p>
		        </div>
		      `);
			this.centerMarker.openInfoWindow(infoWindow);
		},

		clearMarkers() {
			this.markers.forEach((marker) => {
				this.map.removeOverlay(marker);
			});
			this.markers = [];
		},

		handleStoresClick(storesid) {
			const merchant = this.merchants.find((item) => item.id == storesid);
			if (merchant) {
				this.centerMap(merchant);
			} else {
				console.warn(`未找到ID为${storesid}的商户`);
			}
		},

		handleSearch() {
			if (this.filteredMerchants.length === 1) {
				this.centerMap(this.filteredMerchants[0]);
			}
		},

		closeSearchResults() {
			this.searchQuery = '';
			this.$nextTick(() => {
				const input = this.$el.querySelector('.search-input');
				if (input) input.blur();
			});
		},

		centerMap(merchant) {
			this.setCenterMarker(merchant);
			this.closeSearchResults();
		},

		getRandomColor() {
			const colors = ['#007AFF', '#34C759', '#FF9500', '#FF2D55', '#AF52DE', '#5856D6'];
			return colors[Math.floor(Math.random() * colors.length)];
		}
	}
};
</script>

<style scoped>
.merchant-map-container {
	max-width: 1200px;
	margin: 0 auto;
	border-radius: 16px;
	overflow: hidden;
	margin-top: 15px;
	margin-bottom: 45px;
	padding: 0 25px;
}

@media (max-width: 468px) {
	.merchant-map-container {
		padding: 0 15px;
	}
}

.search-bar {
	padding: 15px;
	background: white;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	position: relative; /* 为下拉列表提供定位上下文 */
	z-index: 10; /* 确保搜索栏在地图上方 */
}

.search-bar-content {
	display: flex;
}

@media (max-width: 768px) {
	.search-bar-content {
		display: flex;
		flex-direction: column;
	}
}

.search-input {
	padding: 10px 16px;
	border: 1px solid #dee2e6;
	border-radius: 24px;
	font-size: 14px;
	outline: none;
	transition: all 0.3s;
}

.search-input:focus {
	border-color: #007aff;
	box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.2);
}

.search-button {
	margin-left: 8px;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #007aff;
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	cursor: pointer;
	transition: all 0.2s;
}

.search-button:hover {
	background-color: #0062cc;
	transform: scale(1.05);
}

.map-wrapper {
	flex: 1;
	width: 100%;
	height: 400px;
	position: relative;
}

#baidu-map-container {
	width: 100%;
	height: 400px;
}

.map-background {
	position: relative;
	width: 100%;
	height: 100%;
	background-color: #e9ecef;
	background-image: linear-gradient(#d4d8dc 1px, transparent 1px), linear-gradient(90deg, #d4d8dc 1px, transparent 1px);
	background-size: 20px 20px;
	overflow: hidden;
}

.merchant-marker {
	position: absolute;
	cursor: pointer;
	transition: all 0.2s;
	z-index: 10;
}

.merchant-marker:hover {
	transform: translate(-50%, -100%) scale(1.2);
	z-index: 20;
}

.pin {
	position: relative;
	width: 24px;
	height: 32px;
}

.pin-head {
	position: absolute;
	top: 0;
	left: 50%;
	transform: translateX(-50%);
	width: 16px;
	height: 16px;
	border-radius: 50% 50% 50% 0;
	background-color: #007aff;
	transform: rotate(-45deg);
}

.pin-body {
	position: absolute;
	bottom: 0;
	left: 50%;
	transform: translateX(-50%);
	width: 2px;
	height: 20px;
	background-color: #007aff;
}

.merchant-label {
	position: absolute;
	top: 100%;
	left: 50%;
	transform: translateX(-50%);
	background-color: white;
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 12px;
	white-space: nowrap;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	margin-top: 4px;
}

.map-center-indicator {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	pointer-events: none;
}

.center-dot {
	width: 12px;
	height: 12px;
	background-color: #007aff;
	border-radius: 50%;
	box-shadow: 0 0 0 4px rgba(0, 122, 255, 0.3);
}

/* 商户列表样式优化 */
.merchant-list {
	position: absolute;
	left: 0;
	right: 0;
	top: 100%; /* 从搜索栏底部开始 */
	max-height: 300px;
	overflow-y: auto;
	background: white;
	border-radius: 0 0 8px 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	margin-top: 5px;
	z-index: 20;
}

.merchant-item {
	display: flex;
	align-items: center;
	padding: 10px 15px;
	cursor: pointer;
	transition: all 0.2s;
	border-bottom: 1px solid #f0f0f0;
}

.merchant-item:last-child {
	border-bottom: none;
}

.merchant-item:hover {
	background-color: #f8f9fa;
}

.merchant-icon {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-weight: bold;
	margin-right: 12px;
	flex-shrink: 0;
}

.merchant-info {
	flex: 1;
	min-width: 0; /* 防止内容溢出 */
}

.merchant-name {
	font-weight: 500;
	font-size: 14px;
	margin-bottom: 2px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.merchant-address {
	font-size: 12px;
	color: #6c757d;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
